import type { CSSProperties } from "react";
import type { FoilGradient, FoilVariant } from "@gaole/shared";
import { FOIL_VARIANTS, foilFor } from "@gaole/shared";
import { ListingTitle } from "../ListingTitle";

export type { FoilGradient, FoilVariant };
export { FOIL_VARIANTS, foilFor };

/**
 * A sealed foil pack wrapper — the thing you pick and then tear open. Purely
 * presentational so it can be rendered twice (top half / bottom half) by the
 * rip animation. Squared-off corners and an angled badge plate, like an actual
 * TCG booster pack rather than a generic rounded card.
 */
export function PackFoil({
  variant = "purple",
  gradient,
  name,
  image,
  className = "",
  style,
  sweep = true,
}: {
  variant?: FoilVariant;
  /** When set, overrides the preset variant palette (from BO foil colour). */
  gradient?: FoilGradient;
  name?: string;
  image?: string | null;
  className?: string;
  style?: CSSProperties;
  /** Animated light sweeping across the foil. */
  sweep?: boolean;
}) {
  const c = gradient ?? FOIL_VARIANTS[variant];

  return (
    <div
      className={`relative select-none overflow-hidden ${sweep ? "foil-sweep" : ""} ${className}`}
      style={{
        background: `linear-gradient(155deg, ${c.from} 0%, ${c.via} 42%, ${c.to} 100%)`,
        boxShadow: `inset 0 0 40px rgba(0,0,0,0.45), 0 18px 40px -18px ${c.via}`,
        ...style,
      }}
      aria-hidden
    >
      {/* angled badge plate behind the emblem — the diagonal color-block real
          booster packs use to frame their logo */}
      <div
        className="absolute inset-0"
        style={{
          clipPath: "polygon(0 26%, 100% 10%, 100% 80%, 0 94%)",
          background: `linear-gradient(180deg, ${c.via}66 0%, ${c.ink}00 65%)`,
        }}
      />

      {/* crimped top/bottom edges */}
      <Crimp className="absolute inset-x-0 top-0" ink={c.ink} />
      <Crimp className="absolute inset-x-0 bottom-0 rotate-180" ink={c.ink} />

      {/* tear-strip guide */}
      <div className="absolute inset-x-0 top-[11%] flex items-center gap-1.5 px-3 opacity-75">
        <span className="h-px flex-1" style={{ background: "rgba(255,255,255,0.55)" }} />
        <span className="font-display text-[7px] font-bold uppercase tracking-[0.35em] text-white/85">tear</span>
        <span className="h-px flex-1" style={{ background: "rgba(255,255,255,0.55)" }} />
      </div>

      {/* emblem badge */}
      <div className="absolute inset-x-0 top-[19%] flex flex-col items-center px-4">
        <div className="relative flex aspect-square w-[64%] items-center justify-center">
          {/* sunburst plate — square-locked to this wrapper so it stays circular */}
          <div
            className="absolute inset-[-16%] rounded-full opacity-45"
            style={{
              background: `repeating-conic-gradient(${c.accent}40 0deg 6deg, transparent 6deg 12deg)`,
            }}
          />
          <div
            className="relative flex h-full w-full items-center justify-center rounded-full"
            style={{
              background: `radial-gradient(circle at 38% 28%, ${c.accent}59, ${c.ink} 78%)`,
              boxShadow: `0 0 0 3px rgba(255,255,255,0.4), 0 0 0 6px ${c.ink}aa, inset 0 0 20px rgba(0,0,0,0.5), 0 10px 26px -8px rgba(0,0,0,0.6)`,
            }}
          >
            {image ? (
              <img
                src={image}
                alt=""
                draggable={false}
                className="h-[76%] w-[76%] rounded-full object-cover opacity-95"
              />
            ) : (
              <img src="/logo-mark.png" alt="" draggable={false} className="h-[56%] w-[56%] object-contain drop-shadow" />
            )}
          </div>
        </div>
      </div>

      {/* wordmark */}
      <div className="absolute inset-x-0 bottom-[13%] flex flex-col items-center gap-1.5 px-3 text-center">
        <span className="h-px w-8" style={{ background: "rgba(255,255,255,0.45)" }} />
        <p
          className="font-display text-xs font-extrabold uppercase tracking-[0.28em] text-white"
          style={{ textShadow: `0 2px 10px ${c.ink}` }}
        >
          Mr Gaole
        </p>
        {name && (
          <ListingTitle className="max-w-[85%] text-center text-[9px] font-semibold leading-tight text-white/90">
            {name}
          </ListingTitle>
        )}
      </div>

      <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/25" />
    </div>
  );
}

function Crimp({ className = "", ink }: { className?: string; ink: string }) {
  return (
    <svg viewBox="0 0 240 18" preserveAspectRatio="none" className={`h-[6%] w-full ${className}`} aria-hidden>
      <rect width="240" height="18" fill={ink} fillOpacity="0.55" />
      <g fill="rgba(255,255,255,0.28)">
        {Array.from({ length: 24 }).map((_, i) => (
          <rect key={i} x={i * 10 + 1} y="0" width="4" height="18" />
        ))}
      </g>
      <rect y="15" width="240" height="3" fill="rgba(0,0,0,0.3)" />
    </svg>
  );
}
